<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Data Table Component
      p.text--secondary A powerful data table component based on Vuetify's VDataTable with sorting, pagination, and filtering capabilities.
      .mt-2.pa-3.rounded(style="background-color: #e3f2fd; border-left: 4px solid #2196F3;")
        p.mb-0.text-body-2
          strong Note:&nbsp;
          | When using custom pagination with this component, you can use the YzPagination component with
          code version="yc"
          | for legacy style or
          code version="yz"
          | (default) for modern design. See the
          a.ml-1.cursor-pointer(@click="$router.push('/_design_docs/components/pagination')") Pagination documentation
          |  for details.

    // DataTable Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in dataTableVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            yz-data-table(v-if="variation.id === 'row-selection-custom-slots'" v-model="selectedItems" v-bind="variation.props" item-key="id")
              template(v-slot:header.data-table-select)
                yz-checkbox(:indeterminate="someItemSelected" :value="allItemSelected" @change="toggleSelectAll")

              template(v-slot:item.data-table-select="{ item }")
                yz-checkbox(:value="selectedItems.includes(item)" @change="toggleSelectItem(item)")

            yz-data-table(v-else v-bind="variation.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in dataTableProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      sampleUsers: [
        { id: 1, name: 'John Doe', email: 'john.doe@example.com', role: 'Admin', status: 'Active', lastLogin: '2024-01-15' },
        { id: 2, name: 'Jane Smith', email: 'jane.smith@example.com', role: 'User', status: 'Active', lastLogin: '2024-01-14' },
        { id: 3, name: 'Bob Johnson', email: 'bob.johnson@example.com', role: 'User', status: 'Inactive', lastLogin: '2024-01-10' },
        { id: 4, name: 'Alice Brown', email: 'alice.brown@example.com', role: 'Manager', status: 'Active', lastLogin: '2024-01-16' },
        { id: 5, name: 'Charlie Wilson', email: 'charlie.wilson@example.com', role: 'User', status: 'Active', lastLogin: '2024-01-13' }
      ],
      sampleProducts: [
        { id: 1, name: 'Wireless Headphones', category: 'Electronics', price: 99.99, stock: 45, rating: 4.5 },
        { id: 2, name: 'Coffee Maker', category: 'Appliances', price: 79.99, stock: 23, rating: 4.2 },
        { id: 3, name: 'Running Shoes', category: 'Sports', price: 129.99, stock: 67, rating: 4.8 },
        { id: 4, name: 'Office Chair', category: 'Furniture', price: 299.99, stock: 12, rating: 4.3 },
        { id: 5, name: 'Smartphone', category: 'Electronics', price: 699.99, stock: 8, rating: 4.6 }
      ],
      sampleOrders: [
        { id: '#1001', customer: 'John Doe', total: 299.99, status: 'Completed', date: '2024-01-15' },
        { id: '#1002', customer: 'Jane Smith', total: 149.99, status: 'Processing', date: '2024-01-16' },
        { id: '#1003', customer: 'Bob Johnson', total: 79.99, status: 'Shipped', date: '2024-01-14' },
        { id: '#1004', customer: 'Alice Brown', total: 199.99, status: 'Completed', date: '2024-01-13' }
      ],
      selectedItems: []
    }
  },
  computed: {
    allItemSelected() {
      const itemsForSelection = this.sampleUsers.slice(0, 4)
      return itemsForSelection.length > 0 && this.selectedItems.length === itemsForSelection.length
    },
    someItemSelected() {
      const itemsForSelection = this.sampleUsers.slice(0, 4)
      return this.selectedItems.length > 0 && this.selectedItems.length < itemsForSelection.length
    },
    dataTableVariations() {
      return [
        {
          name: 'Basic Data Table',
          id: 'basic-data-table',
          description: 'Simple data table with basic columns and data',
          props: {
            headers: [
              { text: 'Name', value: 'name' },
              { text: 'Email', value: 'email' },
              { text: 'Role', value: 'role' },
              { text: 'Status', value: 'status' }
            ],
            items: this.sampleUsers.slice(0, 3),
            itemsPerPage: 5
          },
          code: `yz-data-table(
  :headers="headers"
  :items="users"
  :items-per-page="5"
)

// headers example:
headers: [
  { text: 'Name', value: 'name' },
  { text: 'Email', value: 'email' },
  { text: 'Role', value: 'role' },
  { text: 'Status', value: 'status' }
]`
        },
        {
          name: 'Sortable Columns',
          id: 'sortable-columns',
          description: 'Data table with sortable columns enabled',
          props: {
            headers: [
              { text: 'Product', value: 'name', sortable: true },
              { text: 'Category', value: 'category', sortable: false },
              { text: 'Price', value: 'price', sortable: true },
              { text: 'Stock', value: 'stock', sortable: false },
              { text: 'Rating', value: 'rating', sortable: false }
            ],
            items: this.sampleProducts,
            sortBy: 'price',
            sortDesc: true,
            itemsPerPage: 5,
          },
          code: `yz-data-table(
  :headers="headers"
  :items="products"
  :disable-sort="false"
  :sort-by="'price'"
  :sort-desc="true"
  :items-per-page="5"
)

// sortable headers example:
headers: [
  { text: 'Product', value: 'name', sortable: true },
  { text: 'Price', value: 'price', sortable: true }
]`
        },
        {
          name: 'No Data State',
          id: 'no-data-state',
          description: 'Data table showing empty state',
          props: {
            headers: [
              { text: 'Name', value: 'name' },
              { text: 'Email', value: 'email' },
              { text: 'Role', value: 'role' }
            ],
            items: [],
            noDataText: 'No users found',
            itemsPerPage: 5
          },
          code: `yz-data-table(
  :headers="headers"
  :items="[]"
  no-data-text="No users found"
  :items-per-page="5"
)`
        },
        {
          name: 'Loading State',
          id: 'loading-state',
          description: 'Data table in loading state',
          props: {
            headers: [
              { text: 'Name', value: 'name' },
              { text: 'Email', value: 'email' },
              { text: 'Role', value: 'role' }
            ],
            items: this.sampleUsers.slice(0, 3),
            loading: true,
            itemsPerPage: 5
          },
          code: `yz-data-table(
  :headers="headers"
  :items="users"
  :loading="loading"
  :items-per-page="5"
)`
        },
        {
          name: 'Fixed Header',
          id: 'fixed-header',
          description: 'Data table with fixed header and height',
          props: {
            headers: [
              { text: 'Product', value: 'name' },
              { text: 'Category', value: 'category' },
              { text: 'Price', value: 'price' },
              { text: 'Stock', value: 'stock' }
            ],
            items: this.sampleProducts,
            fixedHeader: true,
            height: 100,
            itemsPerPage: -1
          },
          code: `yz-data-table(
  :headers="headers"
  :items="products"
  fixed-header
  :height="300"
  :items-per-page="-1"
)`
        },
        {
          name: 'Show Default Footer',
          id: 'show-default-footer',
          description: 'Data table with default footer enabled',
          props: {
            headers: [
              { text: 'Order ID', value: 'id' },
              { text: 'Customer', value: 'customer' },
              { text: 'Status', value: 'status' }
            ],
            items: this.sampleOrders,
            hideDefaultFooter: false,
            itemsPerPage: 3
          },
          code: `yz-data-table(
  :headers="headers"
  :items="orders"
  :hide-default-footer="false"
  :items-per-page="3"
)`
        },
        {
          name: 'Custom Typography',
          id: 'custom-typography',
          description: 'Data table with custom header and body typography classes',
          props: {
            headers: [
              { text: 'Name', value: 'name' },
              { text: 'Email', value: 'email' },
              { text: 'Role', value: 'role' }
            ],
            items: this.sampleUsers.slice(0, 3),
            headerClass: 'yz-medium-body-md',
            bodyClass: 'yz-regular-body-xs',
            itemsPerPage: -1
          },
          code: `yz-data-table(
  :headers="headers"
  :items="users"
  header-class="yz-medium-body-md"
  body-class="yz-regular-body-xs"
  :items-per-page="-1"
)`
        },
        {
          name: 'Custom Icon Size',
          id: 'custom-sort-icon-size',
          description: 'Data table with custom sortable header sort icon size',
          props: {
            headers: [
              { text: 'Product', value: 'name', sortable: true },
              { text: 'Category', value: 'category', sortable: false },
              { text: 'Price', value: 'price', sortable: true },
              { text: 'Stock', value: 'stock', sortable: true },
              { text: 'Rating', value: 'rating', sortable: false }
            ],
            items: this.sampleProducts,
            disableSort: false,
            sortIconSize: '14px',
            itemsPerPage: 5
          },
          code: `yz-data-table(
  :headers="headers"
  :items="products"
  :disable-sort="false"
  sort-icon-size="14px"
  :items-per-page="5"
)

// You can also use number values:
// sort-icon-size="16"  // converts to 16px

// sortable headers example:
headers: [
  { text: 'Product', value: 'name', sortable: true },
  { text: 'Price', value: 'price', sortable: true },
  { text: 'Stock', value: 'stock', sortable: true }
]`
        },
        {
          name: 'Custom Header Props',
          id: 'custom-header-props',
          description: 'Data table with custom header properties and sort icon configuration',
          props: {
            headers: [
              { text: 'Order ID', value: 'id', sortable: true },
              { text: 'Customer', value: 'customer', sortable: true },
              { text: 'Total', value: 'total', sortable: true },
              { text: 'Status', value: 'status', sortable: false }
            ],
            items: this.sampleOrders,
            disableSort: false,
            headerProps: { sortIcon: 'mdi-arrow-up' },
            sortIconSize: '12px',
            itemsPerPage: -1
          },
          code: `yz-data-table(
  :headers="headers"
  :items="orders"
  :disable-sort="false"
  :header-props="{ sortIcon: 'mdi-arrow-up-down' }"
  sort-icon-size="12px"
  :items-per-page="-1"
)

// header props configuration:
headerProps: {
  sortIcon: 'mdi-arrow-up-down',
  // other header options...
}`
        },
        {
          name: 'Row Selection with Custom Slots',
          id: 'row-selection-custom-slots',
          description: 'Data table with row selection using custom v-slot templates for checkboxes',
          props: {
            headers: [
              { text: 'Name', value: 'name' },
              { text: 'Email', value: 'email' },
              { text: 'Role', value: 'role' },
              { text: 'Status', value: 'status' }
            ],
            items: this.sampleUsers.slice(0, 4),
            showSelect: true,
            itemsPerPage: -1
          },
          code: `template(lang="pug")
  yz-data-table(
    v-model="selectedItems"
    :headers="headers"
    :items="users"
    show-select
    item-key="id"
    :items-per-page="-1"
  )
    template(v-slot:header.data-table-select)
      yz-checkbox(
        :indeterminate="someItemSelected"
        :value="allItemSelected"
        @change="toggleSelectAll"
      )

    template(v-slot:item.data-table-select="{ item }")
      yz-checkbox(
        :value="selectedItems.includes(item)"
        @change="toggleSelectItem(item)"
      )

script
export default {
  data() {
    return {
      selectedItems: [],
      users: [
        { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'Active' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'Active' }
      ]
    }
  },
  computed: {
    allItemSelected() {
      return this.users.length > 0 && this.selectedItems.length === this.users.length
    },
    someItemSelected() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.users.length
    }
  },
  methods: {
    toggleSelectAll() {
      if (this.allItemSelected) {
        this.selectedItems = []
      } else {
        this.selectedItems = [...this.users]
      }
    },
    toggleSelectItem(item) {
      const index = this.selectedItems.findIndex(selected => selected.id === item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item)
      }
    }
  }
}`
        },
        {
          name: 'Custom Styling',
          id: 'custom-styling',
          description: 'Data table with custom colors, radius, and cell heights',
          props: {
            headers: [
              { text: 'Product', value: 'name' },
              { text: 'Price', value: 'price' },
              { text: 'Stock', value: 'stock' }
            ],
            items: this.sampleProducts.slice(0, 3),
            headerBackgroundColor: 'yz-blue-100',
            bodyBackgroundColor: 'yz-neutral-200',
            borderColor: 'yz-neutral-500',
            radius: '12px',
            headerHeight: '40px',
            bodyHeight: '44px',
            itemsPerPage: -1
          },
          code: `yz-data-table(
  :headers="headers"
  :items="products"
  header-background-color="yz-blue-500"
  body-background-color="yz-neutral-200"
  border-color="yz-neutral-500"
  radius="12px"
  header-height="40px"
  body-height="44px"
  :items-per-page="-1"
)`
        }
      ]
    },
    dataTableProps() {
      return [
        {
          name: 'headers',
          type: 'Array',
          default: '[]',
          description: 'Array of header objects defining columns. Each header should have text, value, and optional properties like sortable'
        },
        {
          name: 'items',
          type: 'Array',
          default: '[]',
          description: 'Array of data objects to display in the table'
        },
        {
          name: 'itemsPerPage',
          type: 'Number',
          default: '10',
          description: 'Number of items to show per page. Use -1 to show all items'
        },
        {
          name: 'sortBy',
          type: 'String | Array',
          default: '[]',
          description: 'Column key(s) to sort by initially'
        },
        {
          name: 'sortDesc',
          type: 'Boolean | Array',
          default: 'false',
          description: 'Whether to sort in descending order initially'
        },
        {
          name: 'multiSort',
          type: 'Boolean',
          default: 'false',
          description: 'Enable sorting by multiple columns'
        },
        {
          name: 'mustSort',
          type: 'Boolean',
          default: 'false',
          description: 'Force at least one column to be sorted'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Show loading state with progress indicator'
        },
        {
          name: 'loadingText',
          type: 'String',
          default: 'Loading... Please wait',
          description: 'Text to show during loading state'
        },
        {
          name: 'noDataText',
          type: 'String',
          default: 'No data available',
          description: 'Text to show when no data is available'
        },
        {
          name: 'fixedHeader',
          type: 'Boolean',
          default: 'false',
          description: 'Make table header sticky when scrolling'
        },
        {
          name: 'height',
          type: 'Number | String',
          default: 'undefined',
          description: 'Height of the table container'
        },
        {
          name: 'disableSort',
          type: 'Boolean',
          default: 'false',
          description: 'Disable sorting functionality for all columns. When true, prevents sorting on all columns regardless of individual column sortable settings'
        },
        {
          name: 'hideDefaultFooter',
          type: 'Boolean',
          default: 'true',
          description: 'Hide the default pagination footer'
        },
        {
          name: 'hideDefaultHeader',
          type: 'Boolean',
          default: 'false',
          description: 'Hide the default table header'
        },
        {
          name: 'showExpand',
          type: 'Boolean',
          default: 'false',
          description: 'Show expand/collapse buttons for rows'
        },
        {
          name: 'showSelect',
          type: 'Boolean',
          default: 'false',
          description: 'Show selection checkboxes for rows'
        },
        {
          name: 'singleSelect',
          type: 'Boolean',
          default: 'false',
          description: 'Allow only single row selection'
        },
        {
          name: 'search',
          type: 'String',
          default: 'undefined',
          description: 'Search term to filter table data'
        },
        {
          name: 'serverItemsLength',
          type: 'Number',
          default: '-1',
          description: 'Total number of items available on server (for server-side pagination)'
        },
        {
          name: 'options',
          type: 'Object',
          default: '{}',
          description: 'Pagination, sorting, and grouping options object'
        },
        {
          name: 'headerClass',
          type: 'String',
          default: 'yz-medium-body-xs',
          description: 'Typography class applied to header cells'
        },
        {
          name: 'bodyClass',
          type: 'String',
          default: 'yz-medium-body-sm',
          description: 'Typography class applied to body cells'
        },
        {
          name: 'radius',
          type: 'String | Number',
          default: '10px',
          description: 'Border radius for the table container'
        },
        {
          name: 'headerBackgroundColor',
          type: 'String',
          default: 'yz-neutral-200',
          description: 'Background color for header cells (YZ design token or CSS color)'
        },
        {
          name: 'bodyBackgroundColor',
          type: 'String',
          default: 'white',
          description: 'Background color for body cells (YZ design token or CSS color)'
        },
        {
          name: 'borderColor',
          type: 'String',
          default: 'yz-neutral-300',
          description: 'Border color for table elements (YZ design token or CSS color)'
        },
        {
          name: 'headerHeight',
          type: 'String | Number',
          default: '32px',
          description: 'Height of header cells'
        },
        {
          name: 'bodyHeight',
          type: 'String | Number',
          default: '40px',
          description: 'Height of body cells'
        },
        {
          name: 'sortIconSize',
          type: 'String | Number',
          default: '8px',
          description: 'Size of sortable header icons. Accepts string values (like "12px", "1rem") or number values (converted to pixels)'
        },
        {
          name: 'headerProps',
          type: 'Object',
          default: "{ sortIcon: '$yzi-triangle' }",
          description: 'Configuration object for header properties including custom sort icons and other header options'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    toggleSelectAll() {
      if (this.allItemSelected) {
        this.selectedItems = []
      } else {
        this.selectedItems = [...this.sampleUsers.slice(0, 4)]
      }
    },
    toggleSelectItem(item) {
      const index = this.selectedItems.findIndex(selected => selected.id === item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: white;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>